import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingUp, Copy, StickyNote, Info } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import JeaSelect from "@/components/jea/JeaSelect";
import MoneyInput from "@/components/jea/MoneyInput";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { formatMoney } from "@/lib/pricing";
import { useRolePermissions } from "@/hooks/useRolePermissions";

const todayISO = () => new Date().toISOString().slice(0, 10);
const oneYearAgoISO = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

const emptyState = {
  debtorId: "",
  principalCents: 0,
  ratePct: "5.00",
  startDate: oneYearAgoISO(),
  endDate: todayISO(),
  interestType: "simple",
};

const InterestCalculatorPage = () => {
  const { canWriteNotes } = useRolePermissions();
  const [debtors, setDebtors] = useState([]);
  const [debtorsLoading, setDebtorsLoading] = useState(true);
  const [state, setState] = useState(emptyState);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/debtors");
        setDebtors((data.debtors || []).filter((d) => d.status !== "archived"));
      } catch (e) { /* fine — page still works without debtors */ }
      setDebtorsLoading(false);
    })();
  }, []);

  const debtorOptions = useMemo(
    () => [
      { value: "", label: "— None (Custom calculation) —" },
      ...debtors.map((d) => ({
        value: d.id,
        label: `${d.full_name} · ${d.judgment?.case_number || "—"}`,
      })),
    ],
    [debtors]
  );

  const selectDebtor = (id) => {
    if (!id) {
      setState((s) => ({ ...s, debtorId: "" }));
      return;
    }
    const d = debtors.find((x) => x.id === id);
    if (!d) return;
    setState((s) => ({
      ...s,
      debtorId: id,
      principalCents: d.judgment?.amount_cents || 0,
      ratePct: String(d.judgment?.interest_rate ?? "5.00"),
      startDate: (d.judgment?.date_entered || s.startDate).slice(0, 10),
    }));
  };

  const reset = () => { setState(emptyState); setResult(null); };

  const calculate = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/interest-calculator/calculate", {
        principal_cents: Math.max(0, Math.round(Number(state.principalCents) || 0)),
        rate_pct: parseFloat(state.ratePct) || 0,
        start_date: state.startDate,
        end_date: state.endDate,
        interest_type: state.interestType,
      });
      setResult(data);
      if (state.debtorId) {
        try {
          await apiClient.post("/interest-calculator/save", {
            debtor_id: state.debtorId,
            principal_cents: Math.round(Number(state.principalCents) || 0),
            rate_pct: parseFloat(state.ratePct) || 0,
            start_date: state.startDate,
            end_date: state.endDate,
            interest_type: state.interestType,
          });
        } catch { /* non-blocking */ }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const saveToNote = async () => {
    if (!result || !state.debtorId) return;
    try {
      await apiClient.post("/interest-calculator/save-to-note", {
        debtor_id: state.debtorId,
        principal_cents: result.principal_cents,
        rate_pct: result.rate_pct,
        start_date: result.start_date,
        end_date: result.end_date,
        interest_type: result.interest_type,
        accrued_cents: result.accrued_cents,
        current_total_cents: result.current_total_cents,
        total_days: result.total_days,
      });
      toast.success("Saved as a judgment note.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = async () => {
    if (!result) return;
    const text =
      `Principal: ${formatMoney(result.principal_cents)}\n` +
      `Rate: ${result.rate_pct}% (${result.interest_type === "compound_annual" ? "compound annual" : "simple"})\n` +
      `Period: ${result.start_date} → ${result.end_date} (${result.time_label}, ${result.total_days} days)\n` +
      `Accrued interest: ${formatMoney(result.accrued_cents)}\n` +
      `Current total value: ${formatMoney(result.current_total_cents)}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch { toast.error("Clipboard unavailable."); }
  };

  // Sparkline data — evenly spaced principal-growth series
  const spark = useMemo(() => {
    if (!result) return [];
    const points = 12;
    const out = [];
    for (let i = 0; i <= points; i += 1) {
      const t = i / points;
      out.push({ i, v: Math.round(result.principal_cents + result.accrued_cents * t) });
    }
    return out;
  }, [result]);

  return (
    <div className="space-y-6" data-testid="interest-calculator-page">
      <div className="space-y-1">
        <div className="jea-caption">Tools</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Interest Calculator</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
        {/* LEFT: input */}
        <JeaCard data-testid="interest-input-card">
          <p className="text-text-secondary text-[14px] mb-6">
            Calculate accrued interest on a judgment or arbitrary principal amount.
          </p>

          <div className="space-y-4">
            <div>
              <div className="jea-caption mb-1.5">Judgment Reference (optional)</div>
              {debtorsLoading ? (
                <Skeleton className="h-11" />
              ) : (
                <JeaSelect
                  value={state.debtorId}
                  onChange={selectDebtor}
                  options={debtorOptions}
                  placeholder="— None (Custom calculation) —"
                  data-testid="interest-debtor-picker"
                />
              )}
            </div>

            <div>
              <div className="jea-caption mb-1.5">Principal Amount</div>
              <MoneyInput
                placeholder="$0.00"
                valueCents={state.principalCents}
                onChange={(c) => setState((s) => ({ ...s, principalCents: c }))}
                testid="interest-principal"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="jea-caption mb-1.5 flex items-center gap-1">
                  Interest Rate (%)
                  <span
                    title="Post-judgment rate per state statute. Common: 4–12%."
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] text-text-secondary hover:text-accent-pink cursor-help"
                  >
                    <Info size={12} />
                  </span>
                </div>
                <JeaInput
                  type="text"
                  inputMode="decimal"
                  value={state.ratePct}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d.]/g, "");
                    setState((s) => ({ ...s, ratePct: raw }));
                  }}
                  data-testid="interest-rate"
                />
              </div>
              <div>
                <div className="jea-caption mb-1.5">Interest Type</div>
                <JeaSelect
                  value={state.interestType}
                  onChange={(v) => setState((s) => ({ ...s, interestType: v }))}
                  options={[
                    { value: "simple", label: "Simple Interest" },
                    { value: "compound_annual", label: "Compound — Annual" },
                  ]}
                  data-testid="interest-type"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="jea-caption mb-1.5">Start Date</div>
                <JeaInput
                  type="date"
                  value={state.startDate}
                  onChange={(e) => setState((s) => ({ ...s, startDate: e.target.value }))}
                  data-testid="interest-start-date"
                />
              </div>
              <div>
                <div className="jea-caption mb-1.5">End Date</div>
                <JeaInput
                  type="date"
                  value={state.endDate}
                  onChange={(e) => setState((s) => ({ ...s, endDate: e.target.value }))}
                  data-testid="interest-end-date"
                />
              </div>
            </div>
            <div className="text-text-muted text-[12px]">Leave end date as today for interest accrued to now.</div>

            <div className="pt-2 space-y-2">
              <JeaButton fullWidth onClick={calculate} loading={busy} data-testid="interest-calculate">
                Calculate
              </JeaButton>
              <button
                type="button"
                onClick={reset}
                className="text-text-secondary hover:text-accent-pink text-[13px] w-full text-center"
                data-testid="interest-reset"
              >
                Reset
              </button>
            </div>
          </div>
        </JeaCard>

        {/* RIGHT: result */}
        <JeaCard
          className={result ? "border-accent-pink/40" : ""}
          data-testid="interest-result-card"
        >
          {!result ? (
            <div className="min-h-[300px] flex flex-col items-center justify-center text-center">
              <Calculator size={48} className="text-accent-pink mb-3" />
              <div className="text-text-secondary text-[14px]">
                Enter values on the left and click Calculate.
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
              data-testid="interest-result-populated"
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={24} className="text-accent-pink" />
                <h2 className="text-accent-pink font-bold text-[18px]">Calculation Result</h2>
              </div>

              <div className="grid grid-cols-2 gap-y-3 gap-x-6 mb-5">
                <ResultRow label="Principal" value={formatMoney(result.principal_cents)} valueClass="text-accent-pink text-[16px] font-semibold jea-mono" testid="res-principal" />
                <ResultRow label="Interest Rate" value={`${result.rate_pct}%`} testid="res-rate" />
                <ResultRow label="Time Period" value={result.time_label} testid="res-time" />
                <ResultRow label="Total Days" value={String(result.total_days)} testid="res-days" />
                <ResultRow label="Daily Rate" value={formatMoney(result.daily_rate_cents)} testid="res-daily" />
                <ResultRow label="Accrued Interest" value={formatMoney(result.accrued_cents)} valueClass="text-success text-[16px] font-bold jea-mono" testid="res-accrued" />
              </div>

              <div className="pt-4 border-t border-border-default">
                <div className="text-text-secondary text-[11px] font-semibold tracking-[0.08em] uppercase">Current Total Value</div>
                <div className="jea-mono text-accent-pink text-[28px] font-extrabold" data-testid="res-total">
                  {formatMoney(result.current_total_cents)}
                </div>
              </div>

              <div className="h-24 mt-4" data-testid="interest-sparkline">
                <ResponsiveContainer>
                  <LineChart data={spark}>
                    <XAxis dataKey="i" hide />
                    <YAxis hide domain={["dataMin", "dataMax"]} />
                    <Tooltip
                      contentStyle={{ background: "#0D1E36", border: "1px solid #1E3A58", borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => formatMoney(v)}
                      labelFormatter={() => ""}
                    />
                    <Line type="monotone" dataKey="v" stroke="#fb6f92" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {state.debtorId && canWriteNotes && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <JeaButton size="sm" variant="outline-pink" onClick={saveToNote} className="gap-2" data-testid="interest-save-note">
                    <StickyNote size={14} /> Save to Notes
                  </JeaButton>
                  <JeaButton size="sm" variant="outline-neutral" onClick={copy} className="gap-2" data-testid="interest-copy">
                    <Copy size={14} /> Copy
                  </JeaButton>
                </div>
              )}
              {(!state.debtorId || !canWriteNotes) && (
                <div className="mt-4">
                  <JeaButton size="sm" variant="outline-neutral" onClick={copy} className="gap-2 w-full" data-testid="interest-copy">
                    <Copy size={14} /> Copy Summary
                  </JeaButton>
                </div>
              )}
            </motion.div>
          )}
        </JeaCard>
      </div>
    </div>
  );
};

const ResultRow = ({ label, value, valueClass = "text-text-secondary jea-mono text-[14px]", testid }) => (
  <div data-testid={testid}>
    <div className="text-text-muted text-[10px] font-semibold tracking-[0.08em] uppercase">{label}</div>
    <div className={valueClass}>{value}</div>
  </div>
);

export default InterestCalculatorPage;
